import { useContext, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';

import {
  Flex,
  Text,
  useColorModeValue,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Box,
  Button,
  Spinner,
  IconButton,
  Tooltip,
} from '@chakra-ui/react';

import { AddIcon, RepeatIcon } from '@chakra-ui/icons';

import Paginate from 'components/paginate/Paginate';
import Card from 'components/card/Card';

import { apiGetBranches } from 'api';

import type { APIPaginatedResponse, APIBranchData } from 'types/api.types';

import { AuthContext } from 'contexts/AuthContext';

import { ROLE_ADMIN } from 'variables/constants';

const TABLE_HEADERS = ['Name'];

const BranchListView = () => {
  const { user } = useContext(AuthContext);
  const [page, setPage] = useState(0);
  const navigate = useNavigate();

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const hoverBgColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  const { error, data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['branch-list', page],
    queryFn: () => apiGetBranches(page + 1, ''),
  }) as {
    data: APIPaginatedResponse<APIBranchData>;
    isLoading: boolean;
    error: any;
    isFetching: boolean;
    refetch: () => void;
  };

  const handleEditBranch = (branch: APIBranchData) => {
    navigate(`/admin/settings/branches/edit/${branch.id}`);
  };

  const handleCreateBranch = () => {
    navigate('/admin/settings/branches/new');
  };

  if (error) {
    return (
      <Card>
        <Box p="20px" pb="0">
          <Alert status="error">
            <AlertIcon />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{JSON.stringify(error)}</AlertDescription>
          </Alert>
        </Box>
      </Card>
    );
  }

  return (
    <Card
      flexDirection="column"
      w="100%"
      px="0px"
      overflowX={{ sm: 'scroll', lg: 'hidden' }}
    >
      <Flex px="20px" justifyContent="space-between" align="center" mb="20px">
        <Text
          color={textColor}
          fontSize="22px"
          fontWeight="700"
          lineHeight="100%"
        >
          Branches
        </Text>
        <Flex gap={2}>
          <Tooltip label="Refresh branches" hasArrow>
            <IconButton
              aria-label="Refresh branches"
              icon={<RepeatIcon />}
              onClick={refetch}
              variant="ghost"
              isLoading={isLoading || isFetching}
            />
          </Tooltip>
          {user.role === ROLE_ADMIN && (
            <Button
              leftIcon={<AddIcon />}
              variant="brand"
              size="md"
              onClick={handleCreateBranch}
            >
              New Branch
            </Button>
          )}
        </Flex>
      </Flex>

      <Table variant="simple" color="gray.500">
        <Thead>
          <Tr>
            {TABLE_HEADERS.map((header) => (
              <Th
                key={header}
                pe="10px"
                borderColor={borderColor}
                color="gray.400"
              >
                {header}
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {isLoading && (
            <Tr>
              <Td colSpan={TABLE_HEADERS.length} textAlign="center">
                <Flex align="center" justify="center" mt="20px">
                  <Spinner size="sm" mr="10px" /> Loading branches...
                </Flex>
              </Td>
            </Tr>
          )}

          {data?.results.map((branch) => (
            <Tr
              key={branch.id}
              cursor="pointer"
              _hover={{ bg: hoverBgColor }}
              onClick={() => handleEditBranch(branch)}
            >
              <Td>
                <Flex align="center">
                  <Text color={textColor} fontSize="sm" fontWeight="700">
                    {branch.name}
                  </Text>
                </Flex>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      {data && data.results.length === 0 && (
        <Box textAlign="center" w="100%" mt="20px">
          No branches found
        </Box>
      )}

      <Paginate page={page} count={data?.count || 0} onSetPage={setPage} />
    </Card>
  );
};

export default BranchListView;
